import React, { useState } from 'react';
import { UserIcon, MailIcon, MessageSquareIcon, BellIcon, EditIcon, SaveIcon, XIcon } from 'lucide-react';
import { UserNotificationPreference, NotificationEvent } from './NotificationsManagement';
import { formatDateTime } from '../../utils/dateFormatter';
interface UserPreferencesProps {
  userPreferences: UserNotificationPreference[];
  setUserPreferences: React.Dispatch<React.SetStateAction<UserNotificationPreference[]>>;
  events: NotificationEvent[];
  locale: string;
}
export function UserPreferences({
  userPreferences,
  setUserPreferences,
  events,
  locale
}: UserPreferencesProps) {
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<UserNotificationPreference>>({});
  const handleEditUser = (userId: string) => {
    const user = userPreferences.find(pref => pref.userId === userId);
    if (user) {
      setEditFormData({
        ...user
      });
      setEditingUserId(userId);
    }
  };
  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditFormData({});
  };
  const handleSavePreferences = () => {
    if (!editingUserId || !editFormData) return;
    setUserPreferences(userPreferences.map(pref => pref.userId === editingUserId ? {
      ...pref,
      ...editFormData,
      updatedAt: new Date().toISOString()
    } : pref));
    setEditingUserId(null);
    setEditFormData({});
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    setEditFormData({
      ...editFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  const handleToggleEvent = (eventId: string) => {
    const mutedEvents = editFormData.mutedEvents || [];
    if (mutedEvents.includes(eventId)) {
      setEditFormData({
        ...editFormData,
        mutedEvents: mutedEvents.filter(id => id !== eventId)
      });
    } else {
      setEditFormData({
        ...editFormData,
        mutedEvents: [...mutedEvents, eventId]
      });
    }
  };
  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'teams':
        return <MessageSquareIcon className="h-5 w-5 text-blue-500" data-id="element-19477" />;
      case 'slack':
        return <MessageSquareIcon className="h-5 w-5 text-purple-500" data-id="element-19478" />;
      case 'email':
        return <MailIcon className="h-5 w-5 text-gray-500" data-id="element-19479" />;
      case 'in-app':
        return <BellIcon className="h-5 w-5 text-indigo-500" data-id="element-19480" />;
      default:
        return <BellIcon className="h-5 w-5 text-gray-500" data-id="element-19481" />;
    }
  };
  return <div data-id="element-19482">
      <div className="mb-6" data-id="element-19483">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19484">
          User Notification Preferences
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-19485">
          Configure default notification preferences for users
        </p>
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-19486">
        <ul className="divide-y divide-gray-200" data-id="element-19487">
          {userPreferences.length === 0 ? <li className="px-6 py-10 text-center" data-id="element-19488">
              <p className="text-sm text-gray-500" data-id="element-19489">
                No user preferences configured yet.
              </p>
            </li> : userPreferences.map(pref => <li key={pref.id} data-id="element-19490">
                {editingUserId === pref.userId ? <div className="px-4 py-4 sm:px-6" data-id="element-19491">
                    <div className="flex justify-between items-center mb-4" data-id="element-19492">
                      <div className="flex items-center" data-id="element-19493">
                        <UserIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-19494" />
                        <h4 className="text-sm font-medium text-gray-900" data-id="element-19495">
                          {pref.userName}
                        </h4>
                      </div>
                      <div className="flex space-x-2" data-id="element-19496">
                        <button type="button" onClick={handleSavePreferences} className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19497">
                          <SaveIcon className="h-4 w-4" aria-hidden="true" data-id="element-19498" />
                        </button>
                        <button type="button" onClick={handleCancelEdit} className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500" data-id="element-19499">
                          <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-19500" />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-19501">
                      <div data-id="element-19502">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19503">
                          Preferred Channel
                        </label>
                        <select name="preferredChannel" value={editFormData.preferredChannel} onChange={handleChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-19504">
                          <option value="teams" data-id="element-19505">Microsoft Teams</option>
                          <option value="slack" data-id="element-19506">Slack</option>
                          <option value="email" data-id="element-19507">Email</option>
                          <option value="in-app" data-id="element-19508">In-App</option>
                        </select>
                      </div>
                    </div>
                    <div className="space-y-3 mb-4" data-id="element-19509">
                      <div className="relative flex items-start" data-id="element-19510">
                        <div className="flex items-center h-5" data-id="element-19511">
                          <input id="receive-mentions" name="receiveMentions" type="checkbox" checked={editFormData.receiveMentions} onChange={handleChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-19512" />
                        </div>
                        <div className="ml-3 text-sm" data-id="element-19513">
                          <label htmlFor="receive-mentions" className="font-medium text-gray-700" data-id="element-19514">
                            Receive Mentions
                          </label>
                          <p className="text-gray-500" data-id="element-19515">
                            Notifications when you are mentioned in notes or
                            comments
                          </p>
                        </div>
                      </div>
                      <div className="relative flex items-start" data-id="element-19516">
                        <div className="flex items-center h-5" data-id="element-19517">
                          <input id="receive-approvals" name="receiveApprovalReminders" type="checkbox" checked={editFormData.receiveApprovalReminders} onChange={handleChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-19518" />
                        </div>
                        <div className="ml-3 text-sm" data-id="element-19519">
                          <label htmlFor="receive-approvals" className="font-medium text-gray-700" data-id="element-19520">
                            Receive Approval Reminders
                          </label>
                          <p className="text-gray-500" data-id="element-19521">
                            Notifications for documents requiring your approval
                          </p>
                        </div>
                      </div>
                      <div className="relative flex items-start" data-id="element-19522">
                        <div className="flex items-center h-5" data-id="element-19523">
                          <input id="receive-alerts" name="receiveSystemAlerts" type="checkbox" checked={editFormData.receiveSystemAlerts} onChange={handleChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-19524" />
                        </div>
                        <div className="ml-3 text-sm" data-id="element-19525">
                          <label htmlFor="receive-alerts" className="font-medium text-gray-700" data-id="element-19526">
                            Receive System Alerts
                          </label>
                          <p className="text-gray-500" data-id="element-19527">
                            Notifications for system events and maintenance
                          </p>
                        </div>
                      </div>
                    </div>
                    <div data-id="element-19528">
                      <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-19529">
                        Muted Events
                      </h5>
                      <div className="space-y-2 max-h-40 overflow-y-auto" data-id="element-19530">
                        {events.map(event => <div key={event.id} className="relative flex items-start" data-id="element-19531">
                            <div className="flex items-center h-5" data-id="element-19532">
                              <input id={`mute-event-${event.id}`} type="checkbox" checked={(editFormData.mutedEvents || []).includes(event.id)} onChange={() => handleToggleEvent(event.id)} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-19533" />
                            </div>
                            <div className="ml-3 text-sm" data-id="element-19534">
                              <label htmlFor={`mute-event-${event.id}`} className="font-medium text-gray-700" data-id="element-19535">
                                {event.name}
                              </label>
                            </div>
                          </div>)}
                      </div>
                    </div>
                  </div> : <div className="px-4 py-4 sm:px-6" data-id="element-19536">
                    <div className="flex items-center justify-between" data-id="element-19537">
                      <div className="flex items-center" data-id="element-19538">
                        <UserIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-19539" />
                        <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-19540">
                          {pref.userName}
                        </p>
                      </div>
                      <div data-id="element-19541">
                        <button type="button" onClick={() => handleEditUser(pref.userId)} className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19542">
                          <EditIcon className="h-4 w-4" aria-hidden="true" data-id="element-19543" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 sm:flex sm:justify-between" data-id="element-19544">
                      <div className="sm:flex" data-id="element-19545">
                        <p className="flex items-center text-sm text-gray-500" data-id="element-19546">
                          <MailIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" aria-hidden="true" data-id="element-19547" />
                          {pref.userEmail}
                        </p>
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0" data-id="element-19548">
                        <div className="flex items-center" data-id="element-19549">
                          {getChannelIcon(pref.preferredChannel)}
                          <span className="ml-1 capitalize" data-id="element-19550">
                            {pref.preferredChannel === 'in-app' ? 'In-App' : pref.preferredChannel}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2" data-id="element-19551">
                      <div className="flex space-x-2" data-id="element-19552">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${pref.receiveMentions ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-19553">
                          {pref.receiveMentions ? 'Mentions On' : 'Mentions Off'}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${pref.receiveApprovalReminders ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-19554">
                          {pref.receiveApprovalReminders ? 'Approvals On' : 'Approvals Off'}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${pref.receiveSystemAlerts ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-19555">
                          {pref.receiveSystemAlerts ? 'Alerts On' : 'Alerts Off'}
                        </span>
                      </div>
                    </div>
                    {pref.mutedEvents.length > 0 && <div className="mt-2" data-id="element-19556">
                        <p className="text-xs text-gray-500" data-id="element-19557">
                          Muted events: {pref.mutedEvents.length}
                        </p>
                      </div>}
                    {pref.updatedAt && <div className="mt-2" data-id="element-19558">
                        <p className="text-xs text-gray-500" data-id="element-19559">
                          Last updated: {formatDateTime(pref.updatedAt, locale)}
                        </p>
                      </div>}
                  </div>}
              </li>)}
        </ul>
      </div>
    </div>;
}